'use client';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { useLocale } from '../../hooks/useLocale';
import { toRelativeDate } from '@cademy-package/dates';

const SERVER_DEFAULT_TIMEZONE = 'Europe/London';

const DEFAULT_TITLE_FORMAT = { dateStyle: 'full', timeStyle: 'long' } as const;

export type LocalDateProps = {
    date: Temporal.Instant | string;
    format: Omit<Intl.DateTimeFormatOptions, 'timeZone'> | 'relative';
    titleFormat?: Omit<Intl.DateTimeFormatOptions, 'timeZone'> | 'relative' | 'default';
};

export const LocalDate: FunctionComponent<LocalDateProps> = ({
    date: _date,
    format,
    titleFormat,
}) => {
    const { locale } = useLocale();
    const [timeZone, setTimeZone] = useState(SERVER_DEFAULT_TIMEZONE);
    const date = useMemo(() => {
        return Temporal.Instant.from(_date);
    }, [_date]);

    const relativeDate = useMemo(() => {
        return toRelativeDate(date);
    }, [date]);

    const formattedTitle = useMemo(() => {
        if (!titleFormat) {
            return undefined;
        }

        switch (titleFormat) {
            case 'default': {
                return date.toLocaleString(locale, DEFAULT_TITLE_FORMAT);
            }

            case 'relative': {
                return relativeDate;
            }

            default: {
                return date.toLocaleString(locale, titleFormat);
            }
        }
    }, [date, locale, relativeDate, titleFormat]);

    const formattedLocalDate = useMemo(() => {
        if (format === 'relative') {
            return relativeDate;
        }
        return date.toLocaleString(locale, {
            ...format,
            timeZone: timeZone,
        });
    }, [date, format, timeZone, locale, relativeDate]);

    useEffect(() => {
        setTimeZone(new Intl.DateTimeFormat().resolvedOptions().timeZone);
    }, []);

    return (
        <time title={formattedTitle} dateTime={date.toString()}>
            {formattedLocalDate}
        </time>
    );
};
